import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiThumbsUp, FiThumbsDown,
  FiMessageSquare, FiRefreshCw,
} from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import '../../styles/ValChoiceClassificationSearch.css';


const getToken     = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken')    || '';

const US_STATES = [
  { label: 'Select state', value: '' },
  { label: 'Alaska', value: 'AK' }, { label: 'Alabama', value: 'AL' },
  { label: 'Arkansas', value: 'AR' }, { label: 'Arizona', value: 'AZ' },
  { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' }, { label: 'District of Columbia', value: 'DC' },
  { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' },
  { label: 'Iowa', value: 'IA' }, { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' },
  { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' }, { label: 'Massachusetts', value: 'MA' },
  { label: 'Maryland', value: 'MD' }, { label: 'Maine', value: 'ME' },
  { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' },
  { label: 'Missouri', value: 'MO' }, { label: 'Mississippi', value: 'MS' },
  { label: 'Montana', value: 'MT' }, { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' }, { label: 'Nebraska', value: 'NE' },
  { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' }, { label: 'Nevada', value: 'NV' },
  { label: 'New York', value: 'NY' }, { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' }, { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' }, { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' }, { label: 'Virginia', value: 'VA' },
  { label: 'Vermont', value: 'VT' }, { label: 'Washington', value: 'WA' },
  { label: 'Wisconsin', value: 'WI' }, { label: 'West Virginia', value: 'WV' },
  { label: 'Wyoming', value: 'WY' },
];

interface NowCertsClient {
  name: string;
  databaseId: string;
  insuredType: number;
}

interface CarrierItem {
  name: string;
  naicNumber: number;
}

const ValChoiceClassificationSearch: React.FC = () => {
  /* ── Client search ── */
  const [clientSearch, setClientSearch]   = useState('');
  const [clientName, setClientName]       = useState('');
  const [clientList, setClientList]       = useState<NowCertsClient[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError]     = useState('');

  /* ── Carrier search ── */
  const [carrierSearch, setCarrierSearch]   = useState('');
  const [carrierName, setCarrierName]       = useState('');
  const [carrierList, setCarrierList]       = useState<CarrierItem[]>([]);
  const [carrierLoading, setCarrierLoading] = useState(false);
  const [carrierError, setCarrierError]     = useState('');
  const [naicNumber, setNaicNumber]         = useState(0);

  /* ── Form fields ── */
  const [state, setState]                         = useState('');
  const [stateError, setStateError]               = useState('');
  const [threshold, setThreshold]                 = useState('65');
  const [thresholdError, setThresholdError]       = useState('');
  const [firstName, setFirstName]                 = useState('');
  const [firstNameError, setFirstNameError]       = useState('');
  const [lastName, setLastName]                   = useState('');

  /* ── Agency info ── */
  const [agencyName, setAgencyName] = useState('');
  const [agencyPhone, setAgencyPhone] = useState('');

  /* ── Balance ── */

  /* ── Report ── */
  const [loading, setLoading]     = useState(false);
  const [pdfUrl, setPdfUrl]       = useState('');
  const [reportId, setReportId]   = useState('');

  /* ── Feedback ── */
  const [feedbackOpen, setFeedbackOpen]         = useState(false);
  const [feedbackText, setFeedbackText]         = useState('');
  const [feedbackRating, setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor]     = useState<{ bottom: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError]       = useState('');
  const feedbackRef  = useRef<HTMLDivElement>(null);
  const feedbackBtnRef = useRef<HTMLButtonElement>(null);

  const { showSuccess, showError } = useToast();

  const clientDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const carrierDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Fetch balance + agency on mount ── */
  useEffect(() => {
    fetchAgencySettings();
  }, []);

  /* ── Client search debounce ── */
  useEffect(() => {
    if (!clientSearch.trim()) { setClientList([]); return; }
    if (clientDebounce.current) clearTimeout(clientDebounce.current);
    setClientLoading(true);
    clientDebounce.current = setTimeout(() => fetchClients(clientSearch), 500);
    return () => { if (clientDebounce.current) clearTimeout(clientDebounce.current); };
  }, [clientSearch]);

  /* ── Carrier search debounce ── */
  useEffect(() => {
    if (!carrierSearch.trim()) { setCarrierList([]); return; }
    if (carrierDebounce.current) clearTimeout(carrierDebounce.current);
    setCarrierLoading(true);
    carrierDebounce.current = setTimeout(() => fetchCarriers(carrierSearch), 500);
    return () => { if (carrierDebounce.current) clearTimeout(carrierDebounce.current); };
  }, [carrierSearch]);

  /* ── Close feedback on outside click ── */
  useEffect(() => {
    if (!feedbackOpen) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackOpen]);

  const fetchAgencySettings = async () => {
    try {
      const res = await axios.get(`${(process.env.REACT_APP_UserService_Url      || '')}/api/user/amsagencysettings`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setAgencyName(res.data.agencyName || '');
      setAgencyPhone(res.data.phone || '');
    } catch { /* silent */ }
  };

  const fetchClients = async (query: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  const fetchCarriers = async (query: string) => {
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/valchoice/valchoicecarriercodes`,
        { search: query },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setCarrierList(res.data || []);
    } catch { setCarrierList([]); }
    finally { setCarrierLoading(false); }
  };

  const fetchInsuredContact = async (insuredId: string) => {
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/valchoice/insuredcontact?NCtoken=${getNcToken()}`,
        { insuredDatabaseId: insuredId },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (res.data.length > 0 && res.data[0].firstName) {
        setFirstName(res.data[0].firstName);
        setLastName(res.data[0].lastName || '');
        setFirstNameError('');
      } else {
        setFirstName('');
        setLastName('');
        showError('No contact details found for this insured.');
      }
    } catch {
      setFirstName('');
      setLastName('');
    }
  };

  const onClientSelect = (client: NowCertsClient) => {
    setClientName(client.name);
    setClientSearch('');
    setClientList([]);
    setClientError('');
    fetchInsuredContact(client.databaseId);
  };

  const onCarrierSelect = (item: CarrierItem) => {
    setCarrierName(item.name);
    setNaicNumber(item.naicNumber);
    setCarrierSearch('');
    setCarrierList([]);
    setCarrierError('');
  };

  const validate = () => {
    let valid = true;
    if (!clientName) { setClientError('Please select a client'); valid = false; }
    else setClientError('');
    if (!state) { setStateError('Please select a state'); valid = false; }
    else setStateError('');
    if (!carrierName) { setCarrierError('Please select a carrier'); valid = false; }
    else setCarrierError('');
    if (!threshold) { setThresholdError('Please enter a threshold'); valid = false; }
    else setThresholdError('');
    if (!firstName) { setFirstNameError('Please enter customer first name'); valid = false; }
    else setFirstNameError('');
    return valid;
  };

  const onGetReport = async () => {
    if (!validate()) return;
    setLoading(true);
    setPdfUrl('');
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/valchoice/valchoicereport`,
        {
          state,
          naic: naicNumber,
          report_type: 'autohome',
          lob: '',
          customer_first_name: firstName,
          customer_last_name: lastName || 'NA',
          threshold: parseInt(threshold, 10),
          agency_name: agencyName,
          agent_phone: agencyPhone,
          insuredName: clientName,
          carrierName,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (res.data.url) {
        setPdfUrl(res.data.url);
        setReportId(res.data.id || '');
        showSuccess('Report generated successfully!');
      } else {
        const errMsg = res.data?.error?.error || 'Failed to generate report.';
        if (errMsg.toLowerCase().includes('credits')) {
          showError(errMsg);
        } else {
          showError(errMsg);
        }
      }
    } catch {
      showError('Unknown error while generating the carrier rating report.');
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    setPdfUrl('');
    setReportId('');
    setClientName('');
    setCarrierName('');
    setNaicNumber(0);
    setState('');
    setThreshold('65');
    setFirstName('');
    setLastName('');
    setFeedbackText('');
    setFeedbackRating(null);
  };

  const openFeedback = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackOpen(true);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
    setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
  };

  const saveRating = (isLiked: boolean | null) => {
    if (!reportId) return;
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/valchoice/valchoiceratings`,
      { Id: reportId, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); }).catch(() => { /* silent */ });
  };

  const onThumbsUp = () => {
    const next: boolean | null = feedbackRating === true ? null : true;
    setFeedbackRating(next);
    saveRating(next);
  };

  const onThumbsDown = () => {
    const next: boolean | null = feedbackRating === false ? null : false;
    setFeedbackRating(next);
    saveRating(next);
  };

  const onSubmitFeedback = () => {
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/valchoice/valchoicefeedback`,
      { Id: reportId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Unknown error while submitting feedback.'); });
  };

  return (
    <>
      <motion.div
        className="valchoice-search__container"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* ── Form ── */}
        {!pdfUrl && (
          <div className="valchoice-search__form">
            <div className="valchoice-search__row">

              {/* Client search */}
              <div className="valchoice-search__field--search">
                <label className="valchoice-search__label">
                  Select Client <span className="valchoice-search__required">*</span>
                </label>
                <div className="cs-client-search">
                  <div
                    className="cs-client-search__input-wrap"
                    style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}
                  >
                    <FiSearch size={14} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      type="text"
                      placeholder={clientName || 'Enter client name'}
                      value={clientSearch}
                      onChange={e => {
                        setClientSearch(e.target.value);
                        if (clientName) { setClientName(''); setFirstName(''); setLastName(''); }
                        if (clientError) setClientError('');
                      }}
                    />
                    {clientLoading && <span className="cs-client-search__spinner" />}
                  </div>
                  {clientList.length > 0 && (
                    <ul className="cs-client-dropdown">
                      {clientList.map(c => (
                        <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => onClientSelect(c)}>
                          <span className="cs-client-dropdown__name">{c.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {clientSearch.trim() && !clientLoading && clientList.length === 0 && (
                    <ul className="cs-client-dropdown">
                      <li className="cs-client-dropdown__item valchoice-search__empty-item">
                        No clients found
                      </li>
                    </ul>
                  )}
                </div>
                {clientName && !clientSearch && (
                  <span className="valchoice-search__selected-label">Client: {clientName}</span>
                )}
                {clientError && <span className="valchoice-search__error-text">{clientError}</span>}
              </div>

              {/* State dropdown */}
              <div className="valchoice-search__field--state">
                <label className="valchoice-search__label">
                  Select State <span className="valchoice-search__required">*</span>
                </label>
                <div className="ho-select-wrap">
                  <select
                    className={`ho-select valchoice-search__select${stateError ? ' ho-input--error' : ''}`}
                    style={{ borderColor: stateError ? 'var(--accent-red, #ef4444)' : undefined }}
                    value={state}
                    onChange={e => { setState(e.target.value); setStateError(''); }}
                  >
                    {US_STATES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                {stateError && <span className="valchoice-search__error-text">{stateError}</span>}
              </div>

              {/* Carrier search */}
              <div className="valchoice-search__field--search">
                <label className="valchoice-search__label">
                  Select Carrier <span className="valchoice-search__required">*</span>
                </label>
                <div className="cs-client-search">
                  <div
                    className="cs-client-search__input-wrap"
                    style={{ borderColor: carrierError ? 'var(--accent-red, #ef4444)' : undefined }}
                  >
                    <FiSearch size={14} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      type="text"
                      placeholder={carrierName || 'Search carrier name'}
                      value={carrierSearch}
                      onChange={e => {
                        setCarrierSearch(e.target.value);
                        if (carrierName) { setCarrierName(''); setNaicNumber(0); }
                        if (carrierError) setCarrierError('');
                      }}
                    />
                    {carrierLoading && <span className="cs-client-search__spinner" />}
                  </div>
                  {carrierList.length > 0 && (
                    <ul className="cs-client-dropdown">
                      {carrierList.map(c => (
                        <li key={c.naicNumber} className="cs-client-dropdown__item" onClick={() => onCarrierSelect(c)}>
                          <span className="cs-client-dropdown__name">{c.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {carrierSearch.trim() && !carrierLoading && carrierList.length === 0 && (
                    <ul className="cs-client-dropdown">
                      <li className="cs-client-dropdown__item valchoice-search__empty-item">
                        No carriers found
                      </li>
                    </ul>
                  )}
                </div>
                {carrierName && !carrierSearch && (
                  <span className="valchoice-search__selected-label">Carrier: {carrierName}</span>
                )}
                {carrierError && <span className="valchoice-search__error-text">{carrierError}</span>}
              </div>
            </div>

            <div className="valchoice-search__row">
              {/* Threshold */}
              <div className="valchoice-search__field--threshold">
                <label className="valchoice-search__label">
                  Threshold <span className="valchoice-search__required">*</span>
                </label>
                <div
                  className="cs-client-search__input-wrap"
                  style={{ borderColor: thresholdError ? 'var(--accent-red, #ef4444)' : undefined }}
                >
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder="e.g. 65"
                    value={threshold}
                    onChange={e => {
                      if (/^\d*$/.test(e.target.value)) { setThreshold(e.target.value); setThresholdError(''); }
                    }}
                  />
                </div>
                {thresholdError && <span className="valchoice-search__error-text">{thresholdError}</span>}
              </div>

              {/* Customer First Name */}
              <div className="valchoice-search__field--name">
                <label className="valchoice-search__label">
                  Customer First Name <span className="valchoice-search__required">*</span>
                </label>
                <div
                  className="cs-client-search__input-wrap"
                  style={{ borderColor: firstNameError ? 'var(--accent-red, #ef4444)' : undefined }}
                >
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={e => { setFirstName(e.target.value); setFirstNameError(''); }}
                  />
                </div>
                {firstNameError && <span className="valchoice-search__error-text">{firstNameError}</span>}
              </div>

              {/* Customer Last Name */}
              <div className="valchoice-search__field--name">
                <label className="valchoice-search__label">
                  Customer Last Name
                </label>
                <div className="cs-client-search__input-wrap">
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Get Report button */}
            <div className="valchoice-search__actions">
              <button
                className="cs-browse-btn cs-browse-btn--sm valchoice-search__submit-btn"
                onClick={onGetReport}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="cs-client-search__spinner valchoice-search__spinner-gap" />
                    Generating…
                  </>
                ) : 'Get Report'}
              </button>
            </div>
          </div>
        )}

        {/* ── PDF Report ── */}
        {pdfUrl && (
          <div className="valchoice-search__report">
            <iframe
              src={pdfUrl}
              className="valchoice-search__iframe"
              title="Carrier Rating Report"
            />
            <div className="valchoice-search__report-actions">
              <button className="cs-browse-btn cs-browse-btn--sm" onClick={onReset}>
                <FiRefreshCw size={13} /> Upload Again
              </button>
              <button
                ref={feedbackBtnRef}
                className="cs-browse-btn cs-browse-btn--sm"
                onClick={openFeedback}
              >
                <FiMessageSquare size={13} /> Feedback
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Feedback popup ── */}
      {feedbackOpen && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ bottom: feedbackAnchor.bottom, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUp}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDown}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedbackError && (
            <p className="csfb-error">{feedbackError}</p>
          )}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedback}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ValChoiceClassificationSearch;

